import React, { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/store';
import { toggleSidebar, toggleSidebarCollapse } from '@/store/slices/uiSlice';
import { TopAppBar } from './AppBar';
import { Sidebar } from './Sidebar';
import { Breadcrumbs } from './Breadcrumbs';
import { PageErrorBoundary } from './PageErrorBoundary';
import { SearchBar } from '@/components/common/SearchBar';
import { AiChatPanel } from '@/components/ai/AiChatPanel';
import { AiChatButton } from '@/components/ai/AiChatButton';
import { useUserLanguage } from '@/modules/profile/useUserLanguage';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 72;

export const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const { sidebarOpen, sidebarCollapsed } = useAppSelector((state) => state.ui);
  const { pathname } = useLocation();
  // Die im Profil gespeicherte Sprache nach der Anmeldung anwenden — einmal je
  // Sitzung. Der Rahmen steht auf jeder Seite; hier greift sie überall.
  useUserLanguage();

  const handleMenuToggle = () => {
    if (isMobile) {
      dispatch(toggleSidebar());
    } else {
      dispatch(toggleSidebarCollapse());
    }
  };

  const drawerWidth = sidebarCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <TopAppBar onMenuToggle={handleMenuToggle} />

      {/* Mobile Sidebar */}
      {isMobile && (
        <Sidebar
          open={sidebarOpen}
          collapsed={false}
          onClose={() => dispatch(toggleSidebar())}
          variant="temporary"
        />
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sidebar
          open={true}
          collapsed={sidebarCollapsed}
          variant="permanent"
        />
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: 0 },
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Breadcrumbs />
        {/*
          Fehlergrenze und Ladekreis je Seite: Kann eine Seite nicht geladen
          werden, steht dort ein Hinweis mit „Neu laden“, und Menü und Kopfzeile
          bleiben bedienbar (Testgrundlage 2.2, T00). Mit dem Pfad beginnt die
          Grenze von vorn — die nächste Seite soll nicht den alten Fehler zeigen.
        */}
        <PageErrorBoundary resetKey={pathname}>
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                <CircularProgress />
              </Box>
            }
          >
            <Outlet />
          </Suspense>
        </PageErrorBoundary>
      </Box>
      {/*
        Der Suchdialog. Er stand fertig im Bestand, wurde aber nirgends
        eingehängt: die Lupe in der Kopfzeile setzte das Flag, und nichts las
        es. Die Volltextsuche war in der laufenden Anwendung nicht erreichbar,
        so gut der Dienst dahinter auch antwortete.
      */}
      <SearchBar />
      {/*
        Der Co-Auditor. Er sitzt bewusst als schwebender Knopf über allem und
        nicht hinter einem Umschalter im Suchfeld: Sechzehn Funktionen, die
        niemand findet, sind so gut wie keine. Er schreibt nichts — er fragt
        den Bestand ab.
      */}
      <AiChatButton />
      <AiChatPanel />
    </Box>
  );
};
